import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DashboardModel } from '../../hooks/useDashboard';
import { formatRelativeAirstripLocation } from '../../hooks/dashboard/dashboardUtils';
import { telemetryApi, type TelemetryEventsPayload } from '../../services/telemetryApi';

type EventRow = {
  timestamp: string;
  action: string;
  type: string;
  lat?: number;
  lng?: number;
  altitude: number | null;
  speed: number;
  locationLabel: string;
  count?: number;
  durationMinutes?: number;
};

const FLIGHT_PHASE = new Set(['Enroute', 'Approach', 'Slowing Down, Possible Approach', 'Slowing Down']);
const HARD_TRANSITION = new Set(['Take Off', 'Landing', 'Start Up', 'Stopped Moving']);

const locBucket = (lat?: number, lng?: number) =>
  lat != null && lng != null ? `${lat.toFixed(2)},${lng.toFixed(2)}` : '--';

/** Condense raw telemetry event rows into operational-level summary */
function groupEventRows(rows: EventRow[]): EventRow[] {
  // Sort chronologically before grouping
  const sorted = [...rows].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const grouped: EventRow[] = [];

  for (const ev of sorted) {
    const last = grouped[grouped.length - 1];
    if (!last) { grouped.push({ ...ev, count: 1, durationMinutes: 0 }); continue; }

    const prevTs = new Date(last.timestamp).getTime();
    const currTs = new Date(ev.timestamp).getTime();
    const dtMin = Math.max(0, (currTs - prevTs) / 60000);

    const isHard = HARD_TRANSITION.has(ev.action);
    const isFlight = FLIGHT_PHASE.has(ev.action);
    const lastFlight = FLIGHT_PHASE.has(last.action);

    // Merge consecutive flight-phase rows (ignore location/speed/alt drift mid-flight)
    if (!isHard && isFlight && lastFlight && dtMin < 12) {
      grouped[grouped.length - 1] = {
        ...ev,
        action: 'Enroute',
        locationLabel: last.locationLabel, // keep origin label
        count: (last.count || 1) + 1,
        durationMinutes: (last.durationMinutes || 0) + dtMin,
      };
      continue;
    }

    // Merge same ground-event at same location bucket (< 6 min gap)
    const sameLoc = locBucket(last.lat, last.lng) === locBucket(ev.lat, ev.lng);
    if (!isHard && !isFlight && last.action === ev.action && sameLoc && dtMin < 6) {
      grouped[grouped.length - 1] = {
        ...ev,
        count: (last.count || 1) + 1,
        durationMinutes: (last.durationMinutes || 0) + dtMin,
      };
      continue;
    }

    grouped.push({ ...ev, count: 1, durationMinutes: 0 });
  }

  // Return newest-first
  return grouped.reverse();
}

const fmt24 = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase();
};

const DashboardHistoryTab: React.FC<DashboardModel> = ({
  dark,
  selectedAircraft,
  aircraftFlightInfo,
  historyDateFrom,
  historyDateTo,
  historyReplayData,
  isLoadingReplay,
  replayIndex,
  isReplaying,
  replaySpeed,
  currentReplayRowRef,
  LEG_COLORS,
  replayStats,
  replayRouteStops,
  getNearestAirstrip,
  mapAirstrips,
  fetchHistoryReplay,
  setHistoryDateFrom,
  setHistoryDateTo,
  setReplayIndex,
  setIsReplaying,
  setReplaySpeed,
}) => {
  const [backendEventsPayload, setBackendEventsPayload] = useState<TelemetryEventsPayload | null>(null);
  const [isLoadingBackendEvents, setIsLoadingBackendEvents] = useState(false);

  const toLocalInput = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const startOfWeek = (d: Date) => {
    const s = startOfDay(d);
    s.setDate(s.getDate() - s.getDay());
    return s;
  };
  const endOfWeek = (d: Date) => {
    const e = startOfWeek(d);
    e.setDate(e.getDate() + 6);
    return endOfDay(e);
  };

  const presetRange = (key: string) => {
    const now = new Date();
    switch (key) {
      case 'today':
        return { from: startOfDay(now), to: now };
      case 'yesterday': {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        return { from: startOfDay(y), to: endOfDay(y) };
      }
      case 'last7': {
        const from = new Date(now);
        from.setDate(from.getDate() - 6);
        return { from: startOfDay(from), to: now };
      }
      case 'thisWeek':
        return { from: startOfWeek(now), to: now };
      case 'lastWeek': {
        const lastWeekRef = new Date(now);
        lastWeekRef.setDate(lastWeekRef.getDate() - 7);
        return { from: startOfWeek(lastWeekRef), to: endOfWeek(lastWeekRef) };
      }
      case 'last30': {
        const from = new Date(now);
        from.setDate(from.getDate() - 29);
        return { from: startOfDay(from), to: now };
      }
      case 'thisMonth': {
        const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return { from, to: now };
      }
      case 'lastMonth': {
        const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        const to = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
        return { from, to };
      }
      case 'thisYear': {
        const from = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        return { from, to: now };
      }
      case 'lastYear': {
        const from = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
        const to = endOfDay(new Date(now.getFullYear() - 1, 11, 31));
        return { from, to };
      }
      default:
        return { from: startOfDay(now), to: now };
    }
  };

  const presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 Days' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'last30', label: 'Last 30 Days' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'thisYear', label: 'This Year' },
    { key: 'lastYear', label: 'Last Year' },
  ];

  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [historyView, setHistoryView] = useState<'timeline' | 'events'>('timeline');
  const [tempDateFrom, setTempDateFrom] = useState(historyDateFrom);
  const [tempDateTo, setTempDateTo] = useState(historyDateTo);
  const rangeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 900 });

  useEffect(() => {
    if (!isRangeOpen) {
      setTempDateFrom(historyDateFrom);
      setTempDateTo(historyDateTo);
    }
  }, [historyDateFrom, historyDateTo, isRangeOpen]);

  useEffect(() => {
    if (!selectedAircraft?.tailNumber || !historyDateFrom || !historyDateTo) {
      setBackendEventsPayload(null);
      return;
    }
    let mounted = true;
    const from = new Date(historyDateFrom);
    const to = new Date(historyDateTo);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setBackendEventsPayload(null);
      return;
    }

    setIsLoadingBackendEvents(true);
    telemetryApi
      .getEvents(selectedAircraft.tailNumber, from.toISOString(), to.toISOString(), 5000)
      .then((data) => {
        if (!mounted) return;
        if (data && Array.isArray(data.events) && data.events.length > 0) {
          setBackendEventsPayload(data);
        } else {
          setBackendEventsPayload(null);
        }
      })
      .catch(() => {
        if (mounted) setBackendEventsPayload(null);
      })
      .finally(() => {
        if (mounted) setIsLoadingBackendEvents(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedAircraft?.tailNumber, historyDateFrom, historyDateTo]);

  const backendEventRows = useMemo<EventRow[]>(() => {
    if (!backendEventsPayload?.events?.length) return [];
    const raw: EventRow[] = backendEventsPayload.events.map((ev) => {
      const speed = Number(ev.groundSpeedKt || 0);
      const action = ev.label || ev.eventType || 'Event';
      const isFlight =
        String(ev.eventType || '').toUpperCase().includes('TAKEOFF') ||
        String(ev.eventType || '').toUpperCase().includes('AIRBORNE') ||
        speed >= 50;
      return {
        timestamp: ev.timestamp,
        action,
        type: isFlight ? 'FLIGHT' : 'GROUND',
        lat: typeof ev.lat === 'number' ? ev.lat : undefined,
        lng: typeof ev.lng === 'number' ? ev.lng : undefined,
        altitude: typeof ev.altitudeFt === 'number' ? ev.altitudeFt : null,
        speed,
        locationLabel: ev.locationLabel || '',
      };
    });
    return groupEventRows(raw);
  }, [backendEventsPayload]);

  const usingBackendEvents = backendEventRows.length > 0;

  useEffect(() => {
    if (!isRangeOpen) return;
    const updatePos = () => {
      const rect = rangeButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(1100, Math.max(760, window.innerWidth - 48));
      const left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
      const top = rect.bottom + 8;
      setPanelPos({ top, left, width });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isRangeOpen]);

  const selectedRangeLabel = useMemo(() => {
    if (!historyDateFrom || !historyDateTo) return 'Select date range';
    const from = new Date(historyDateFrom);
    const to = new Date(historyDateTo);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Select date range';
    const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${from.toLocaleDateString('en-US', fmt)} - ${to.toLocaleDateString('en-US', fmt)}`;
  }, [historyDateFrom, historyDateTo]);

  return (
    <div className={`flex-1 flex flex-col overflow-visible ${dark ? 'bg-slate-900/45' : 'bg-slate-50/70'}`}>
      <div className={`relative px-3 py-2.5 border-b overflow-x-auto overflow-y-visible ${dark ? 'bg-slate-900/56 border-blue-200/10' : 'bg-white/75 border-slate-200/70'}`}>
        <div className={`flex items-center gap-2 flex-nowrap rounded-xl px-2 py-1.5 border ${dark ? 'bg-slate-900/80 border-blue-200/15 shadow-[inset_0_1px_0_rgba(125,211,252,0.08)]' : 'bg-white/90 border-slate-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]'}`}>
        {[
          { key: 'today', label: 'Today' },
          { key: 'yesterday', label: 'Yesterday' },
          { key: 'thisWeek', label: 'This Week' },
          { key: 'lastWeek', label: 'Last Week' },
        ].map(({ label }) => (
          <button key={label} onClick={() => {
            const key = (label === 'Today' ? 'today'
              : label === 'Yesterday' ? 'yesterday'
              : label === 'Last 7 Days' ? 'last7'
              : label === 'This Week' ? 'thisWeek'
              : label === 'Last Week' ? 'lastWeek'
              : 'last30');
            const { from, to } = presetRange(key);
            setHistoryDateFrom(toLocalInput(from));
            setHistoryDateTo(toLocalInput(to));
          }} className={`text-[8px] font-black uppercase px-2 py-1 rounded-md border transition-colors ${dark ? 'border-blue-200/20 text-slate-200 hover:bg-blue-400/10' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
            {label}
          </button>
        ))}
        <button
          ref={rangeButtonRef}
          type="button"
          onClick={() => setIsRangeOpen((v) => !v)}
          className={`text-[8px] font-black uppercase px-2 py-1 rounded-md border transition-colors ${dark ? 'border-indigo-300/45 text-indigo-200 hover:bg-indigo-400/12' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'}`}
        >
          Date Range
        </button>
        <span className={`flex-1 min-w-0 truncate text-[9px] font-bold ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{selectedRangeLabel}</span>
        <button onClick={fetchHistoryReplay} disabled={isLoadingReplay || !historyDateFrom}
          className={`shrink-0 text-[9px] font-black uppercase px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 ${
            dark ? 'bg-indigo-500/85 hover:bg-indigo-500 text-indigo-50 border border-indigo-300/40' : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}>
          {isLoadingReplay ? 'Loading…' : 'Load'}
        </button>
        </div>
        {isRangeOpen && createPortal(
          <div className="fixed inset-0 z-[1200]">
            <div className="absolute inset-0" onClick={() => setIsRangeOpen(false)} />
            <div
              style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
              className={`absolute rounded-xl border shadow-2xl overflow-hidden ${dark ? 'bg-slate-900 border-indigo-400/30' : 'bg-white border-slate-200'}`}
            >
            <div className="grid grid-cols-[220px_1fr] min-h-[280px]">
              <div className={`${dark ? 'bg-slate-950 border-r border-indigo-400/20' : 'bg-slate-50 border-r border-slate-200'} p-3`}>
                <div className="space-y-1">
                  {presets.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => {
                        const { from, to } = presetRange(p.key);
                        setTempDateFrom(toLocalInput(from));
                        setTempDateTo(toLocalInput(to));
                      }}
                      className={`w-full text-left px-2 py-1.5 text-[11px] font-bold rounded ${dark ? 'text-slate-200 hover:bg-slate-800' : 'text-slate-700 hover:bg-white'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className={`text-[10px] font-black uppercase ${dark ? 'text-indigo-300' : 'text-slate-500'}`}>From</p>
                    <input
                      type="datetime-local"
                      value={tempDateFrom}
                      onChange={(e) => setTempDateFrom(e.target.value)}
                      className={`w-full text-[11px] rounded px-2 py-1.5 border outline-none focus:ring-1 focus:ring-indigo-400 ${dark ? 'bg-slate-800 border-indigo-400/30 text-indigo-100' : 'bg-white border-slate-200 text-slate-800'}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className={`text-[10px] font-black uppercase ${dark ? 'text-indigo-300' : 'text-slate-500'}`}>To</p>
                    <input
                      type="datetime-local"
                      value={tempDateTo}
                      onChange={(e) => setTempDateTo(e.target.value)}
                      className={`w-full text-[11px] rounded px-2 py-1.5 border outline-none focus:ring-1 focus:ring-indigo-400 ${dark ? 'bg-slate-800 border-indigo-400/30 text-indigo-100' : 'bg-white border-slate-200 text-slate-800'}`}
                    />
                  </div>
                </div>
                <div className={`mt-4 text-[10px] font-bold ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {(() => {
                    if (!tempDateFrom || !tempDateTo) return 'Select a range';
                    const from = new Date(tempDateFrom);
                    const to = new Date(tempDateTo);
                    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Select a range';
                    return `${from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  })()}
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setHistoryDateFrom(tempDateFrom);
                      setHistoryDateTo(tempDateTo);
                      setIsRangeOpen(false);
                    }}
                    className="text-[11px] font-black uppercase px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTempDateFrom(historyDateFrom);
                      setTempDateTo(historyDateTo);
                      setIsRangeOpen(false);
                    }}
                    className={`text-[11px] font-black uppercase px-3 py-1.5 rounded border ${dark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      <div className={`px-4 py-1.5 border-b flex items-center gap-1.5 ${dark ? 'bg-slate-900/55 border-blue-200/10' : 'bg-slate-50/80 border-slate-200/70'}`}>
        <button
          type="button"
          onClick={() => setHistoryView('timeline')}
          className={`text-[8px] font-black uppercase px-2.5 py-1 rounded border ${historyView === 'timeline'
            ? (dark ? 'border-amber-300/40 bg-amber-400/10 text-amber-300' : 'border-indigo-300 bg-indigo-50 text-indigo-700')
            : (dark ? 'border-slate-700 text-slate-400 hover:text-slate-200' : 'border-slate-200 text-slate-500 hover:text-slate-700')}`}
        >
          Timeline
        </button>
        <button
          type="button"
          onClick={() => setHistoryView('events')}
          className={`text-[8px] font-black uppercase px-2.5 py-1 rounded border ${historyView === 'events'
            ? (dark ? 'border-amber-300/40 bg-amber-400/10 text-amber-300' : 'border-indigo-300 bg-indigo-50 text-indigo-700')
            : (dark ? 'border-slate-700 text-slate-400 hover:text-slate-200' : 'border-slate-200 text-slate-500 hover:text-slate-700')}`}
        >
          Events
        </button>
      </div>

      {historyView === 'timeline' && historyReplayData.length > 0 && (
        <div className={`px-4 py-2 border-b flex items-center gap-3 ${dark ? 'bg-gradient-to-r from-slate-900/82 to-slate-800/62 border-blue-200/15' : 'bg-gradient-to-r from-white to-slate-50 border-slate-200/80'}`}>
          <button onClick={() => { setReplayIndex(0); setIsReplaying(false); }}
            className={`text-[11px] px-2 py-1 rounded-md ${dark ? 'bg-slate-700/90 text-slate-200 hover:bg-slate-600' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>⏮</button>
          <button onClick={() => setIsReplaying(p => !p)}
            className={`text-[9px] font-black uppercase px-3 py-1.5 rounded-md shadow-sm ${isReplaying ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'}`}>
            {isReplaying ? '⏸ Pause' : '▶ Play'}
          </button>
          <select value={replaySpeed} onChange={e => setReplaySpeed(Number(e.target.value))}
            className={`text-[9px] font-bold rounded-md px-2 py-1 border ${dark ? 'bg-slate-800 border-blue-200/25 text-slate-100' : 'bg-white border-slate-200 text-slate-800'}`}>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
            <option value={4}>4×</option>
            <option value={8}>8×</option>
          </select>
          <span className={`text-[9px] font-mono rounded-md px-2 py-1 ${dark ? 'text-slate-300 bg-slate-800/80 border border-blue-200/10' : 'text-slate-600 bg-white border border-slate-200'}`}>{replayIndex + 1} / {historyReplayData.length}</span>
        </div>
      )}

      {historyView === 'timeline' && replayStats && (() => {
        const pilot = aircraftFlightInfo[selectedAircraft?.tailNumber || '']?.pilotName;
        const curEv = historyReplayData[replayIndex];
        return (
          <div className={`border-b ${dark ? 'border-blue-200/10 bg-gradient-to-b from-slate-900/72 to-slate-900/52' : 'border-slate-200/80 bg-gradient-to-b from-white to-slate-50/40'}`}>
            <div className="flex items-center px-4 pt-2 pb-1 gap-0">
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg mr-3 flex-wrap max-w-[320px] ${dark ? 'bg-slate-800/75 border border-blue-200/10' : 'bg-slate-50 border border-slate-200/80'}`}>
                <svg className={`w-3 h-3 shrink-0 ${dark ? 'text-emerald-400' : 'text-emerald-500'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                {replayRouteStops.length > 0
                  ? replayRouteStops.map((stop: string, si: number) => (
                      <React.Fragment key={si}>
                        {si > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: LEG_COLORS[(si - 1) % LEG_COLORS.length], display: 'inline-block', boxShadow: '0 0 4px currentColor', flexShrink: 0 }}/>
                            <span className={`text-[9px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>→</span>
                          </span>
                        )}
                        <span className={`text-[10px] font-black ${dark ? 'text-white' : 'text-slate-800'}`}>{stop}</span>
                      </React.Fragment>
                    ))
                  : <span className={`text-[10px] font-black ${dark ? 'text-slate-400' : 'text-slate-500'}`}>—</span>
                }
              </div>
              {pilot && (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg mr-3 ${dark ? 'bg-amber-400/10 border border-amber-300/25' : 'bg-amber-50 border border-amber-200'}`}>
                  <svg className={`w-3 h-3 ${dark ? 'text-amber-400' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
                  <span className={`text-[10px] font-bold ${dark ? 'text-amber-300' : 'text-amber-700'}`}>{pilot}</span>
                </div>
              )}
              <div className="flex items-center gap-0 ml-auto">
                {[
                  { label: 'Dist', value: `${replayStats.totalNm} nm`, color: dark ? 'text-white' : 'text-slate-800' },
                  { label: 'Block', value: replayStats.flightTimeMin >= 60 ? `${Math.floor(replayStats.flightTimeMin/60)}h ${replayStats.flightTimeMin%60}m` : `${replayStats.flightTimeMin}m`, color: dark ? 'text-white' : 'text-slate-800' },
                  { label: 'Ldg', value: String(replayStats.landings), color: dark ? 'text-white' : 'text-slate-800' },
                  { label: 'Pts', value: String(replayStats.totalPoints), color: dark ? 'text-slate-400' : 'text-slate-500' },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`flex flex-col items-center px-3 py-1 border-l rounded-md ${dark ? 'border-blue-200/10 bg-slate-900/35' : 'border-slate-200 bg-white/75'}`}>
                    <span className={`text-[7px] font-black uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{label}</span>
                    <span className={`text-[11px] font-black font-mono mt-0.5 ${color}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const pts = historyReplayData;
              const maxAlt = Math.max(...pts.map(p => p.altitude || 0), 1);
              const maxSpd = Math.max(...pts.map(p => p.speed || 0), 1);
              const H = 52;
              const step = Math.max(1, Math.floor(pts.length / 800));
              const sampled = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
              const N = sampled.length - 1;
              const altPts = sampled.map((p, i) => `${(i/N*100).toFixed(2)},${(H - (p.altitude||0)/maxAlt*(H-2)).toFixed(2)}`).join(' ');
              const spdPts = sampled.map((p, i) => `${(i/N*100).toFixed(2)},${(H - (p.speed||0)/maxSpd*(H-2)).toFixed(2)}`).join(' ');
              const curX = (replayIndex / Math.max(pts.length-1, 1) * 100).toFixed(2);
              return (
                <div className={`relative cursor-crosshair mx-0 px-0 rounded-md overflow-hidden ${dark ? 'bg-gradient-to-b from-slate-900/70 to-slate-950/70' : 'bg-gradient-to-b from-slate-50 to-white'}`} style={{ height: `${H+8}px` }} onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                  setIsReplaying(false);
                  setReplayIndex(Math.round(ratio * (pts.length - 1)));
                }}>
                  <svg viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: `${H}px`, display: 'block' }}>
                    <defs>
                      <linearGradient id={`ag${dark?'d':'l'}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3"/>
                        <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02"/>
                      </linearGradient>
                      <linearGradient id={`sg${dark?'d':'l'}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25"/>
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02"/>
                      </linearGradient>
                    </defs>
                    {[0.25,0.5,0.75].map(t => (
                      <line key={t} x1="0" y1={H*t} x2="100" y2={H*t} stroke={dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'} strokeWidth="0.3" vectorEffect="non-scaling-stroke"/>
                    ))}
                    <polygon points={`0,${H} ${altPts} 100,${H}`} fill={`url(#ag${dark?'d':'l'})`}/>
                    <polyline points={altPts} fill="none" stroke="#f59e0b" strokeWidth="1" vectorEffect="non-scaling-stroke"/>
                    <polygon points={`0,${H} ${spdPts} 100,${H}`} fill={`url(#sg${dark?'d':'l'})`}/>
                    <polyline points={spdPts} fill="none" stroke="#60a5fa" strokeWidth="1" vectorEffect="non-scaling-stroke"/>
                    <line x1={curX} y1="0" x2={curX} y2={H} stroke="rgba(255,255,255,0.85)" strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
                    <circle cx={curX} cy={H - (curEv?.altitude||0)/maxAlt*(H-2)} r="2" fill="#f59e0b" vectorEffect="non-scaling-stroke"/>
                    <circle cx={curX} cy={H - (curEv?.speed||0)/maxSpd*(H-2)} r="2" fill="#60a5fa" vectorEffect="non-scaling-stroke"/>
                  </svg>
                  <div className="absolute top-1 left-2 flex items-center gap-3 pointer-events-none">
                    <span className="flex items-center gap-1">
                      <span style={{ width:10, height:2, background:'#f59e0b', display:'inline-block', borderRadius:1 }}/>
                      <span className={`text-[7px] font-bold uppercase tracking-wide ${dark ? 'text-amber-400' : 'text-amber-600'}`}>Alt</span>
                      {curEv?.altitude > 0 && <span className={`text-[8px] font-black font-mono ${dark ? 'text-amber-300' : 'text-amber-700'}`}>{Math.round(curEv.altitude).toLocaleString()}ft</span>}
                    </span>
                    <span className="flex items-center gap-1">
                      <span style={{ width:10, height:2, background:'#60a5fa', display:'inline-block', borderRadius:1 }}/>
                      <span className={`text-[7px] font-bold uppercase tracking-wide ${dark ? 'text-blue-400' : 'text-blue-600'}`}>GS</span>
                      {curEv?.speed > 0 && <span className={`text-[8px] font-black font-mono ${dark ? 'text-blue-300' : 'text-blue-700'}`}>{Math.round(curEv.speed)}kt</span>}
                    </span>
                  </div>
                  <div className="absolute top-1 right-2 flex items-center gap-3 pointer-events-none">
                    <span className={`text-[7px] font-mono ${dark ? 'text-slate-600' : 'text-slate-300'}`}>{replayStats.maxAlt.toLocaleString()}ft max</span>
                    <span className={`text-[7px] font-mono ${dark ? 'text-slate-600' : 'text-slate-300'}`}>{replayStats.maxSpeed}kt max</span>
                  </div>
                  <div className="absolute bottom-0.5 left-2 flex items-center gap-2 pointer-events-none">
                    {[['#94a3b8','Stop'],['#60a5fa','Taxi'],['#22c55e','>50kt'],['#f97316','>100kt'],['#ef4444','>160kt']].map(([c,l]) => (
                      <span key={l} className="flex items-center gap-0.5">
                        <span style={{ width:8, height:2, background:c, display:'inline-block', borderRadius:1 }}/>
                        <span style={{ fontSize:6, color: dark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.8)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>{l}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {historyView === 'timeline' && historyReplayData.length > 0 && (
        <div className={`px-4 py-2 border-b flex items-center gap-3 ${dark ? 'border-blue-200/10 bg-slate-900/45' : 'border-slate-200/80 bg-slate-50/80'}`}>
          <span className={`text-[8px] font-mono shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
            {new Date(historyReplayData[0].timestamp).toLocaleString()}
          </span>
          <input type="range" min={0} max={historyReplayData.length - 1} value={replayIndex}
            onChange={e => { setIsReplaying(false); setReplayIndex(Number(e.target.value)); }}
            className={`flex-1 h-1.5 cursor-pointer ${dark ? 'accent-amber-400' : 'accent-indigo-600'}`} />
          <span className={`text-[8px] font-mono shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
            {new Date(historyReplayData[historyReplayData.length - 1].timestamp).toLocaleString()}
          </span>
        </div>
      )}

      {historyView === 'timeline' && (
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {isLoadingReplay ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : historyReplayData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 gap-1">
              <p className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No telemetry data for this period</p>
              <p className={`text-[9px] ${dark ? 'text-slate-600' : 'text-slate-300'}`}>Try a different date range or use the quick-select buttons above</p>
            </div>
          ) : (
            <div className="flex items-center justify-center h-24">
              <p className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Timeline Loaded</p>
            </div>
          )}
        </div>
      )}

      {historyView === 'events' && (
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {(isLoadingReplay || isLoadingBackendEvents) ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !usingBackendEvents && historyReplayData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 gap-1">
              <p className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No telemetry data for this period</p>
              <p className={`text-[9px] ${dark ? 'text-slate-600' : 'text-slate-300'}`}>Try a different date range or use the quick-select buttons above</p>
            </div>
          ) : (
          <table className="w-full text-left">
            <thead className={`sticky top-0 border-b backdrop-blur ${dark ? 'bg-slate-900/88 border-blue-200/15' : 'bg-white/92 border-slate-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-[8px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-amber-300' : 'text-slate-600'}`}>Time</th>
                <th className={`px-6 py-3 text-[8px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-amber-300' : 'text-slate-600'}`}>Event</th>
                <th className={`px-6 py-3 text-[8px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-amber-300' : 'text-slate-600'}`}>Location</th>
                <th className={`px-6 py-3 text-[8px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-amber-300' : 'text-slate-600'}`}>Altitude</th>
                <th className={`px-6 py-3 text-right pr-8 text-[8px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-amber-300' : 'text-slate-600'}`}>GS</th>
              </tr>
            </thead>
            <tbody className={dark ? 'divide-y divide-blue-200/10' : 'divide-y divide-slate-200/65'}>
              {(usingBackendEvents ? backendEventRows : historyReplayData).map((ev: any, i: number) => {
                const isCurrent = !usingBackendEvents && i === replayIndex;
                const nearbyName = getNearestAirstrip(ev.lat, ev.lng);
                const isFlight = ev.type === 'FLIGHT' || ev.action === 'Enroute';
                const isStationary = ev.action === 'Stationary' || ev.action === 'Stopped Moving';
                const isKeyEvent = ['Take Off', 'Landing', 'Start Up'].includes(ev.action);
                const isMergedFlight = ev.action === 'Enroute' && (ev.count || 1) > 1;
                return (
                  <tr key={i} ref={isCurrent ? currentReplayRowRef : null} onClick={() => {
                    if (usingBackendEvents) return;
                    setIsReplaying(false);
                    setReplayIndex(i);
                  }}
                    className={`cursor-pointer transition-colors ${isStationary ? 'opacity-55' : ''} ${isCurrent
                      ? dark ? 'bg-amber-500/16 border-l-2 border-l-amber-300' : 'bg-indigo-50/80 border-l-2 border-l-indigo-500'
                      : dark ? 'odd:bg-slate-900/25 even:bg-slate-900/12 hover:bg-slate-700/35' : 'odd:bg-white even:bg-slate-50/45 hover:bg-slate-100/60'}`}>
                    <td className={`px-6 py-2.5 ${isKeyEvent ? `border-l-2 ${ev.action === 'Take Off' ? 'border-indigo-500' : ev.action === 'Landing' ? 'border-emerald-500' : 'border-amber-500'}` : 'border-l-2 border-transparent'}`}>
                      <div className="flex flex-col">
                        <span className={`font-mono text-[10px] font-semibold ${isCurrent ? (dark ? 'text-amber-300' : 'text-indigo-600') : isStationary ? (dark ? 'text-slate-500' : 'text-slate-400') : (dark ? 'text-slate-200' : 'text-slate-700')}`}>
                          {fmt24(ev.timestamp)}
                        </span>
                        {isMergedFlight && ev.durationMinutes > 0 && (
                          <span className={`text-[8px] font-bold ${dark ? 'text-indigo-400' : 'text-indigo-500'}`}>{Math.round(ev.durationMinutes)} min</span>
                        )}
                        {!isMergedFlight && (ev.count || 1) > 1 && (
                          <span className="text-[8px] text-slate-400 font-semibold">×{ev.count}{ev.durationMinutes > 0 ? ` · ${Math.round(ev.durationMinutes)}m` : ''}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[8px] font-semibold uppercase tracking-[0.08em] border ${
                        ev.action === 'Take Off'
                          ? (dark ? 'bg-indigo-500/25 text-indigo-200 border-indigo-400/40' : 'bg-indigo-100 text-indigo-700 border-indigo-300')
                        : ev.action === 'Landing'
                          ? (dark ? 'bg-emerald-500/25 text-emerald-200 border-emerald-400/40' : 'bg-emerald-100 text-emerald-700 border-emerald-300')
                        : ev.action === 'Start Up'
                          ? (dark ? 'bg-amber-500/20 text-amber-200 border-amber-400/30' : 'bg-amber-50 text-amber-700 border-amber-200')
                        : isFlight
                          ? (dark ? 'bg-indigo-500/16 border-indigo-300/30 text-indigo-200' : 'bg-indigo-50 border-indigo-200 text-indigo-700')
                        : isStationary
                          ? (dark ? 'bg-slate-700/40 text-slate-400 border-slate-600/20' : 'bg-slate-100/70 text-slate-400 border-slate-200/70')
                        : (dark ? 'bg-amber-500/16 border-amber-300/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-700')
                      }`}>{ev.action}</span>
                    </td>
                    <td className="px-6 py-2.5">
                      {(() => {
                        const rel = formatRelativeAirstripLocation(ev.lat, ev.lng, mapAirstrips);
                        const primary = ev.locationLabel || nearbyName || rel?.primary || `${ev.lat?.toFixed(4)}, ${ev.lng?.toFixed(4)}`;
                        const secondary = rel?.secondary || `${ev.lat?.toFixed(4)}, ${ev.lng?.toFixed(4)}`;
                        return (
                          <div className="flex flex-col leading-tight">
                            <span className={`text-[9px] font-medium ${isCurrent ? (dark ? 'text-amber-300' : 'text-indigo-600') : isStationary ? (dark ? 'text-slate-500' : 'text-slate-400') : (dark ? 'text-slate-200' : 'text-slate-700')}`}>{primary}</span>
                            <span className={`text-[8px] font-mono ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{secondary}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-2.5">
                      <span className={`font-mono text-[10px] font-medium ${isStationary ? (dark ? 'text-blue-400/50' : 'text-blue-400/60') : (dark ? 'text-blue-300' : 'text-blue-700')}`}>
                        {ev.altitude && ev.altitude > 0 ? `${Math.round(ev.altitude).toLocaleString()} ft` : '--'}
                      </span>
                    </td>
                    <td className="px-6 py-2.5 text-right pr-8">
                      <span className={`font-mono text-[10px] font-medium ${isStationary ? (dark ? 'text-emerald-400/50' : 'text-emerald-400/60') : (dark ? 'text-emerald-300' : 'text-emerald-700')}`}>
                        {ev.speed > 0 ? `${Math.round(ev.speed)} kt` : '--'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardHistoryTab;
